import classNames from 'classnames'
import { Fragment, useReducer, useState } from 'react'
import { useParams } from 'react-router-dom'

import type { MouseEvent, ReactElement } from 'react'
import type { IService } from '~/types/application-management'
import type { TPlatformType } from '~/types/device-type'

import TasksExecutionFlow from './TasksExecutionFlow'

import SetAppModal from '../SetAppModal'
import SetServiceModal from '../SetServiceModal'
import UpdateAccountModal from '../UpdateAccountModal'

import AppLogo from '~/components/AppLogo'
import BackNavigation from '~/components/BackNavigation'
import Button from '~/components/Buttons/Button'
import DetailSection from '~/components/DetailSection'
import Layout from '~/components/Layouts/Layout'
import MwMLoader from '~/components/MwMLoader'
import DataTable, { DataTableItem } from '~/components/Tables/DataTable'
import ViewTitle from '~/components/ViewTitle'
import { AUTHORIZATIONS } from '~/const'
import { FASTLY_PATH } from '~/const/asset'
import { useIsUserAuthorized } from '~/providers/Authorizations'
import { useAppsManagementDetailsQuery } from '~/queries/application-management'

import './styles.scss'

interface IServiceReducerState {
  isOpen: boolean
  platform?: TPlatformType
  service?: IService
}

interface IServiceActionReducer {
  payload?: {
    platform: TPlatformType
    service?: IService
  }
  type: 'openModal' | 'closeModal'
}

const servicesReducer = (state: IServiceReducerState, action: IServiceActionReducer): IServiceReducerState => {
  switch (action.type) {
    case 'openModal':
      return { ...state, isOpen: true, platform: action.payload?.platform, service: action.payload?.service }
    case 'closeModal':
      return { ...state, isOpen: false, platform: undefined, service: undefined }
    default:
      return state
  }
}

const AppDetail = (): ReactElement => {
  /** Local state */

  const { appKey } = useParams<{ appKey: string }>()

  const applicationWriteAuthorized = useIsUserAuthorized([AUTHORIZATIONS.VERSION.APPLICATION.WRITE])

  const [isModalOpen, setModalOpen] = useState<{ isOpen: boolean; section: string }>({
    isOpen: false,
    section: 'general',
  })

  const [isUpdateAccountModalOpen, setUpdateAccountModalOpen] = useState(false)

  const [serviceReducerState, serviceReducerDispatch] = useReducer(servicesReducer, {
    isOpen: false,
    platform: undefined,
    service: undefined,
  })

  /** Queries & Mutations */

  const { data: application, isLoading: applicationIsLoading } = useAppsManagementDetailsQuery(appKey)

  /** Handlers */

  const handleOpenModal = (isOpen: boolean, section: string) => () => setModalOpen({ isOpen, section })

  const handleOpenUpdateAccountModal = (isOpen: boolean) => () => setUpdateAccountModalOpen(isOpen)

  const handleAddService = (platform: TPlatformType) => () => {
    serviceReducerDispatch({ payload: { platform }, type: 'openModal' })
  }

  const handleEditService = (platform: TPlatformType, serviceName: string) => (evt: MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation()
    const service = application?.[platform]?.services.find(({ service }) => service === serviceName)
    serviceReducerDispatch({ payload: { platform: platform, service }, type: 'openModal' })
  }

  const handleCloseServiceModal = () => serviceReducerDispatch({ type: 'closeModal' })

  /** Render */

  const account =
    application?.android?.account || application?.ios?.account || application?.hms?.account || application?.web?.account

  return (
    <Layout className="app-details-view">
      <ViewTitle className="layout-view-title flex justify-space-between">
        <BackNavigation title="Apps" to="/apps-management" />
        <div className="flex align-center justify-center">App details</div>
        <div className="flex right" />
      </ViewTitle>
      {applicationIsLoading ? (
        <div className="loader-container">
          <MwMLoader category="item" />
        </div>
      ) : application ? (
        <>
          <div className="app-details-header">
            <AppLogo
              appLogoUrl={application.icon_file_id ? `${FASTLY_PATH}${application.icon_file_id}` : undefined}
              appName={application.default_display_name}
              size="medium"
            />
          </div>
          <div className="app-details-view__body">
            <div className="left-side sections-container">
              <DetailSection title="General">
                <DataTable className="app-details-data-table">
                  <DataTableItem label="App key" value={application.app_key} />
                  <DataTableItem label="Default Cluster ID" value={application.default_cluster_id} />
                  <DataTableItem label="Display name" value={application.default_display_name} />
                  <DataTableItem label="Prefix" value={application.prefix} />
                  <DataTableItem label="Is publishing" value={application.is_publishing ? 'Yes' : 'No'} />
                </DataTable>
                <Button
                  disabled={!applicationWriteAuthorized}
                  ghost
                  onClick={handleOpenModal(true, 'general')}
                  size="small"
                >
                  Edit
                </Button>
              </DetailSection>
              {application.ios ? (
                <DetailSection title="iOS">
                  <div>
                    <div className="stores-section">
                      <DataTable className="app-details-data-table">
                        <DataTableItem label="Store App Key" value={application.ios.app_key} />
                        <DataTableItem label="Store account" value={application.ios.account_key} />
                        <DataTableItem label="Bundle ID" value={application.ios.bundle_id} />
                        <DataTableItem label="Store ID" value={`${application.ios.store_id || '-'}`} />
                        <DataTableItem label="UGS / SKU" value={application.ios.sku} />
                        <DataTableItem label="Store subscription key" value={application.ios.subscription_key} />
                        <DataTableItem label="App Token" value={application.ios.app_token} />
                      </DataTable>
                      <Button
                        disabled={!applicationWriteAuthorized}
                        ghost
                        onClick={handleOpenModal(true, 'ios-configuration')}
                        size="small"
                      >
                        Edit
                      </Button>
                    </div>
                    {application.ios.services.length ? (
                      <>
                        <br />
                        <DataTable className="app-details-data-table stores-section-services">
                          {application.ios.services.map(({ additional_config, enabled, service }) => (
                            <Fragment key={service}>
                              <DataTableItem
                                actionMenu={
                                  <Button
                                    className="edit-service-button"
                                    disabled={!applicationWriteAuthorized}
                                    ghost
                                    onClick={handleEditService('ios', service)}
                                    size="small"
                                  >
                                    Edit
                                  </Button>
                                }
                                className={classNames({ 'service-enabled': enabled })}
                                copyEnabled={false}
                                label="Name"
                                value={service}
                              />
                              {additional_config && additional_config.length > 2 ? (
                                <DataTableItem
                                  className={classNames({ 'service-enabled': enabled })}
                                  isJson
                                  label=""
                                  value={JSON.stringify(JSON.parse(additional_config), null, 2)}
                                />
                              ) : null}
                            </Fragment>
                          ))}
                        </DataTable>
                      </>
                    ) : null}
                  </div>
                  <Button onClick={handleAddService('ios')} size="small">
                    Add service
                  </Button>
                </DetailSection>
              ) : null}
              {application.android ? (
                <DetailSection title="Android">
                  <div>
                    <div className="stores-section">
                      <DataTable className="app-details-data-table">
                        <DataTableItem label="Store App Key" value={application.android.app_key} />
                        <DataTableItem label="Store account" value={application.android.account_key} />
                        <DataTableItem label="Bundle ID" value={application.android.bundle_id} />
                        <DataTableItem label="Store ID" value={application.android.private_store_id} />
                        <DataTableItem label="App Token" value={application.android.app_token} />
                      </DataTable>
                      <Button
                        disabled={!applicationWriteAuthorized}
                        ghost
                        onClick={handleOpenModal(true, 'android-configuration')}
                        size="small"
                      >
                        Edit
                      </Button>
                    </div>
                    {application.android.services.length ? (
                      <>
                        <br />
                        <DataTable className="app-details-data-table stores-section-services">
                          {application.android.services.map(({ additional_config, enabled, service }) => (
                            <Fragment key={service}>
                              <DataTableItem
                                actionMenu={
                                  <Button
                                    className="edit-service-button"
                                    disabled={!applicationWriteAuthorized}
                                    ghost
                                    onClick={handleEditService('android', service)}
                                    size="small"
                                  >
                                    Edit
                                  </Button>
                                }
                                className={classNames({ 'service-enabled': enabled })}
                                copyEnabled={false}
                                label="Name"
                                value={service}
                              />
                              {additional_config && additional_config.length > 2 ? (
                                <DataTableItem
                                  className={classNames({ 'service-enabled': enabled })}
                                  isJson
                                  label=""
                                  value={JSON.stringify(JSON.parse(additional_config), null, 2)}
                                />
                              ) : null}
                            </Fragment>
                          ))}
                        </DataTable>
                      </>
                    ) : null}
                  </div>
                  <Button onClick={handleAddService('android')} size="small">
                    Add service
                  </Button>
                </DetailSection>
              ) : null}
              {application.hms ? (
                <DetailSection title="HMS">
                  <div>
                    <div className="stores-section">
                      <DataTable className="app-details-data-table">
                        <DataTableItem label="Store App Key" value={application.hms.app_key} />
                        <DataTableItem label="Store account" value={application.hms.account_key} />
                        <DataTableItem label="Bundle ID" value={application.hms.bundle_id} />
                        <DataTableItem label="Store ID" value={`${application.hms.store_id || '-'}`} />
                        <DataTableItem label="Store subscription key" value={application.hms.subscription_key} />
                        <DataTableItem label="App Token" value={application.hms.app_token} />
                      </DataTable>
                      <Button
                        disabled={!applicationWriteAuthorized}
                        ghost
                        onClick={handleOpenModal(true, 'hms-configuration')}
                        size="small"
                      >
                        Edit
                      </Button>
                    </div>
                    {application.hms.services.length ? (
                      <>
                        <br />
                        <DataTable className="app-details-data-table stores-section-services">
                          {application.hms.services.map(({ additional_config, enabled, service }) => (
                            <Fragment key={service}>
                              <DataTableItem
                                actionMenu={
                                  <Button
                                    className="edit-service-button"
                                    disabled={!applicationWriteAuthorized}
                                    ghost
                                    onClick={handleEditService('hms', service)}
                                    size="small"
                                  >
                                    Edit
                                  </Button>
                                }
                                className={classNames({ 'service-enabled': enabled })}
                                copyEnabled={false}
                                label="Name"
                                value={service}
                              />
                              {additional_config && additional_config.length > 2 ? (
                                <DataTableItem
                                  className={classNames({ 'service-enabled': enabled })}
                                  isJson
                                  label=""
                                  value={JSON.stringify(JSON.parse(additional_config), null, 2)}
                                />
                              ) : null}
                            </Fragment>
                          ))}
                        </DataTable>
                      </>
                    ) : null}
                  </div>
                  <Button onClick={handleAddService('hms')} size="small">
                    Add service
                  </Button>
                </DetailSection>
              ) : null}
              {application.web ? (
                <DetailSection title="WEB">
                  <div>
                    <div className="stores-section">
                      <DataTable className="app-details-data-table">
                        <DataTableItem label="Url" value={application.web.url} />
                      </DataTable>
                      <Button
                        disabled={!applicationWriteAuthorized}
                        ghost
                        onClick={handleOpenModal(true, 'web-configuration')}
                        size="small"
                      >
                        Edit
                      </Button>
                    </div>
                    {application.web.services.length ? (
                      <>
                        <br />
                        <DataTable className="stores-section-services">
                          {application.web.services.map(({ additional_config, enabled, service }) => (
                            <Fragment key={service}>
                              <DataTableItem
                                actionMenu={
                                  <Button
                                    className="edit-service-button"
                                    disabled={!applicationWriteAuthorized}
                                    ghost
                                    onClick={handleEditService('web', service)}
                                    size="small"
                                  >
                                    Edit
                                  </Button>
                                }
                                className={classNames({ 'service-enabled': enabled })}
                                copyEnabled={false}
                                label="Name"
                                value={service}
                              />
                              {additional_config && additional_config.length > 2 ? (
                                <DataTableItem
                                  className={classNames({ 'service-enabled': enabled })}
                                  isJson
                                  label=""
                                  value={JSON.stringify(JSON.parse(additional_config), null, 2)}
                                />
                              ) : null}
                            </Fragment>
                          ))}
                        </DataTable>
                      </>
                    ) : null}
                  </div>
                  <Button onClick={handleAddService('web')} size="small">
                    Add service
                  </Button>
                </DetailSection>
              ) : null}
              <DetailSection title="Technical">
                <DataTable className="app-details-data-table">
                  <DataTableItem label="Google Cloud Platform ID" value={application.gcp_id} />
                  <DataTableItem label="In-App Purchase pack ID" value={application.default_mwm_iap_pack_id} />
                  <DataTableItem label="Unity technology" value={application.is_unity ? 'Yes' : 'No'} />
                  <DataTableItem label="Lokalize ID" value={application.lokalise_project_id} />
                </DataTable>
                <Button
                  disabled={!applicationWriteAuthorized}
                  ghost
                  onClick={handleOpenModal(true, 'general')}
                  size="small"
                >
                  Edit
                </Button>
              </DetailSection>
              <DetailSection title="Other">
                <DataTable className="app-details-data-table">
                  <DataTableItem label="Support email" value={application.support_email} />
                  <DataTableItem label="Terms of services url" value={application.terms_of_services_url} />
                  <DataTableItem label="Privacy policy url" value={application.privacy_policy_url} />
                </DataTable>
                <Button
                  disabled={!applicationWriteAuthorized}
                  ghost
                  onClick={handleOpenModal(true, 'general')}
                  size="small"
                >
                  Edit
                </Button>
              </DetailSection>
              {account ? (
                <>
                  <DetailSection title="Account">
                    <DataTable className="app-details-data-table">
                      <DataTableItem label="Account key" value={account.account_key} />
                      <DataTableItem label="AppStore Account ID" value={account.app_store_id} />
                      <DataTableItem label="App Store subscription key" value={account.app_store_subscription_key} />
                      <DataTableItem label="Play Store ID" value={account.play_store_id} />
                    </DataTable>
                    <Button
                      disabled={!applicationWriteAuthorized}
                      ghost
                      onClick={handleOpenUpdateAccountModal(true)}
                      size="small"
                    >
                      Edit
                    </Button>
                  </DetailSection>
                  <UpdateAccountModal
                    account={account}
                    isOpen={isUpdateAccountModalOpen}
                    onClose={handleOpenUpdateAccountModal(false)}
                  />
                </>
              ) : null}
            </div>
            <div className="right-side sections-container">
              {application ? <TasksExecutionFlow application={application} /> : null}
            </div>
          </div>
          <SetAppModal
            application={application}
            isOpen={isModalOpen.isOpen}
            onClose={handleOpenModal(false, 'general')}
            section={isModalOpen.section}
          />
          <SetServiceModal
            appKey={application.app_key}
            initialServiceConfig={serviceReducerState.service}
            isOpen={serviceReducerState.isOpen}
            onClose={handleCloseServiceModal}
            platform={serviceReducerState.platform}
          />
        </>
      ) : (
        <div>The application cannot be found :(</div>
      )}
    </Layout>
  )
}

export default AppDetail
